import React from 'react';

import {
  Typography, useMediaQuery, useTheme,
} from '@mui/material';
import {
  Bar,
  BarChart,
  CartesianGrid,
  Line,
  LineChart,
  ResponsiveContainer,
  Tooltip,
  XAxis,
  YAxis,
} from 'recharts';

import { STUDENT_STATES } from '../../../utils/common/constants';
import { ACTIVE_STUDENT_STATES } from '../constants';
import {
  ActiveStudentCount, CountEntry, SimplyCount,
} from '../types';
import { getColorFromYear, getTransparencyFromCourseState } from '../utils';

import {
  Circle, KeyContainer, TooltipContainer, TooltipLabel,
} from './index.styles';

type TooltipProps = {
  payload?: any,
  label?: any,
};

const StudentCountChartTooltip = ({ payload, label }: TooltipProps) => ((
  <TooltipContainer>
    {label}
    {(payload || []).map((entry: any) => (
      <KeyContainer key={entry.name}>
        <Circle style={{ background: entry.color }} />
        <TooltipLabel>
          <Typography variant="subtitle1" color="textSecondary">
            {entry.name}
          </Typography>
          <Typography variant="subtitle1" color="textSecondary">
            {entry.value}
          </Typography>
        </TooltipLabel>
      </KeyContainer>
    ))}
  </TooltipContainer>
));

const TotalEnrolmentCountLineChartTooltip = ({ payload, label }: TooltipProps) => (
  <TooltipContainer>
    {label}
    <TooltipLabel>
      <Typography variant="subtitle1" color="textSecondary">
        {payload[0]?.value} Enrolments
      </Typography>
    </TooltipLabel>
  </TooltipContainer>
);

interface StudentCountByGradYearBarChartProps {
  data?: ActiveStudentCount[],
}

export const StudentCountByGradYearBarChart = ({ data }: StudentCountByGradYearBarChartProps) => {
  const theme = useTheme();
  const isSm = useMediaQuery(theme.breakpoints.up('sm'));

  return (
    <ResponsiveContainer width="100%" height={320}>
      <BarChart
        data={data}
        margin={{
          top: 20,
          bottom: 10,
          left: 5,
          right: 5,
        }}
      >
        <CartesianGrid strokeDasharray="3 3" vertical={false} />
        <Tooltip
          content={StudentCountChartTooltip}
          wrapperStyle={{ zIndex: theme.zIndex.tooltip }}
          cursor={{
            fill: theme.palette.backdrop.dark,
          }}
        />
        <XAxis
          type="category"
          axisLine={false}
          tickLine={false}
          dataKey="gradYear"
          name="Graduation Year"
          tick={{ fill: theme.palette.gray.dark }}
        />
        <YAxis
          type="number"
          axisLine={false}
          tickLine={false}
          tick={{ fill: theme.palette.gray.dark }}
        />
        {ACTIVE_STUDENT_STATES.map((state) => {
          const stateDetails = STUDENT_STATES[state];

          return (
            <Bar
              key={state}
              barSize={isSm ? 50 : 30}
              dataKey={state}
              name={stateDetails.label}
              stackId="a"
              fill={theme.palette[stateDetails.colorKey].main}
            />
          );
        })}
      </BarChart>
    </ResponsiveContainer>
  );
};

interface EnrolmentCountByGradYearAndStateOverTimeBarChartProps {
  data?: CountEntry[],
  xAxisDataKey?: string,
  plottableGradYearStates: string[],
}

export const EnrolmentCountByGradYearAndStateOverTimeBarChart = (
  {
    data,
    xAxisDataKey,
    plottableGradYearStates = [],
  }: EnrolmentCountByGradYearAndStateOverTimeBarChartProps) => {
  const theme = useTheme();

  return (
    <ResponsiveContainer width="100%" height={320}>
      <BarChart
        data={data}
        margin={{
          top: 20,
          bottom: 10,
          left: 5,
          right: 5,
        }}
      >
        <CartesianGrid strokeDasharray="3 3" vertical={false} />
        <Tooltip
          content={StudentCountChartTooltip}
          wrapperStyle={{ zIndex: theme.zIndex.tooltip }}
          cursor={{
            fill: theme.palette.backdrop.dark,
          }}
        />
        <XAxis
          type="category"
          axisLine={false}
          tickLine={false}
          dataKey={xAxisDataKey || 'displayDateWithYear'}
          name="Date"
          tick={{ fill: theme.palette.gray.dark }}
        />
        <YAxis
          type="number"
          axisLine={false}
          tickLine={false}
          tick={{ fill: theme.palette.gray.dark }}
        />
        {plottableGradYearStates.map((gradYearState) => {
          const gradYear = gradYearState.split('#')[0];
          const state = gradYearState.split('#')[1];

          const color = getColorFromYear(gradYear);
          const transparency = getTransparencyFromCourseState(state);

          return (
            <Bar
              key={gradYearState}
              dataKey={gradYearState}
              name={gradYearState}
              stackId="a"
              fill={`${color}${transparency}`}
            />
          );
        })}
      </BarChart>
    </ResponsiveContainer>
  );
};

interface EnrolmentCountYoYOverTimeLineChartProps {
  data?: CountEntry[],
  xAxisDataKey?: string,
  plottableSampleYears: string[],
}

export const EnrolmentCountYoYOverTimeLineChart = (
  {
    data,
    xAxisDataKey,
    plottableSampleYears,
  }: EnrolmentCountYoYOverTimeLineChartProps,
) => {
  const theme = useTheme();

  return (
    <ResponsiveContainer width="100%" height={320}>
      <LineChart
        data={data}
        margin={{
          top: 20,
          bottom: 10,
          left: 5,
          right: 5,
        }}
      >
        <CartesianGrid strokeDasharray="3 3" vertical={false} />
        <Tooltip
          content={StudentCountChartTooltip}
          wrapperStyle={{ zIndex: theme.zIndex.tooltip }}
          cursor={{
            fill: theme.palette.backdrop.dark,
          }}
        />
        <XAxis
          axisLine={false}
          tickLine={false}
          dataKey={xAxisDataKey || 'displayDateWithYear'}
          name="Date"
          tick={{ fill: theme.palette.gray.dark }}
        />
        <YAxis
          type="number"
          axisLine={false}
          tickLine={false}
          tick={{ fill: theme.palette.gray.dark }}
        />
        {plottableSampleYears.map((year) => (
          <Line
            key={year}
            dataKey={year}
            type="monotone"
            name={year}
            stroke={getColorFromYear(year)}
          />
        ))}
      </LineChart>
    </ResponsiveContainer>
  );
};

interface EnrolmentCountOverTimeLineChartProps {
  data?: SimplyCount[],
  xAxisDataKey?: string,
}

export const EnrolmentCountOverTimeLineChart = (
  { data, xAxisDataKey }: EnrolmentCountOverTimeLineChartProps,
) => {
  const theme = useTheme();

  return (
    <ResponsiveContainer width="100%" height={320}>
      <LineChart
        data={data}
        margin={{
          top: 20,
          bottom: 10,
          left: 5,
          right: 5,
        }}
      >
        <CartesianGrid strokeDasharray="3 3" vertical={false} />
        <Tooltip
          content={TotalEnrolmentCountLineChartTooltip}
          wrapperStyle={{ zIndex: theme.zIndex.tooltip }}
          cursor={{
            fill: theme.palette.backdrop.dark,
          }}
        />
        <XAxis
          axisLine={false}
          tickLine={false}
          dataKey={xAxisDataKey || 'displayDateWithYear'}
          name="Date"
          tick={{ fill: theme.palette.gray.dark }}
        />
        <YAxis
          type="number"
          axisLine={false}
          tickLine={false}
          tick={{ fill: theme.palette.gray.dark }}
        />
        <Line type="monotone" dataKey="count" stroke={theme.palette.primary.main} />
      </LineChart>
    </ResponsiveContainer>
  );
};
